import React, { useMemo, useState, useCallback } from 'react';
import { LogEntry, LogLocation } from '../types';
import { parseAnsi, ansiStyleToCss, hasAnsiCodes, AnsiSegment } from '../utils/ansiParser';

// Regex to match file paths with line:column (e.g., src/App.tsx:42:10 or /path/to/file.ts(15,20))
const filePathRegex = /([a-zA-Z0-9_\-./]+\.[a-zA-Z]{1,4})(?:[:(](\d+)(?:[,:](\d+))?[)]?)?/g;

type ParsedSegment = {
  type: 'text' | 'path';
  content: string;
  path?: string;
  line?: number;
  column?: number;
};

function parseMessageForPaths(message: string): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  filePathRegex.lastIndex = 0;

  while ((match = filePathRegex.exec(message)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: message.slice(lastIndex, match.index),
      });
    }

    // Add the file path match
    const [fullMatch, filePath, lineStr, colStr] = match;
    segments.push({
      type: 'path',
      content: fullMatch,
      path: filePath,
      line: lineStr ? parseInt(lineStr, 10) : undefined,
      column: colStr ? parseInt(colStr, 10) : undefined,
    });

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < message.length) {
    segments.push({
      type: 'text',
      content: message.slice(lastIndex),
    });
  }

  return segments.length > 0 ? segments : [{ type: 'text', content: message }];
}

type LogListProps = {
  logs: LogEntry[];
  getLocationFromLog: (log: LogEntry) => LogLocation | null;
  onNavigateLocation: (loc: LogLocation) => void;
  variant?: 'console' | 'terminal';
  height?: number;
};

export const LogList = React.memo(function LogList({
  logs,
  getLocationFromLog,
  onNavigateLocation,
  variant = 'console',
  height,
}: LogListProps) {
  const itemSize = variant === 'terminal' ? 34 : 72;
  const listHeight = height || (variant === 'terminal' ? 220 : 240);
  const [scrollTop, setScrollTop] = useState(0);
  const data = useMemo(() => logs, [logs]);
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);
  const totalHeight = data.length * itemSize;
  const overscan = 6;
  const startIndex = Math.max(0, Math.floor(scrollTop / itemSize) - overscan);
  const endIndex = Math.min(data.length, Math.ceil((scrollTop + listHeight) / itemSize) + overscan);
  const visible = data.slice(startIndex, endIndex);

  const Row = ({ log, idx }: { log: LogEntry; idx: number }) => {
    const loc = getLocationFromLog(log);
    const clickable = !!loc;
    const handleClick = () => {
      if (loc) onNavigateLocation(loc);
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!loc) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onNavigateLocation(loc);
      }
    };

    // Render a text segment with clickable file paths
    const renderPathSegment = (text: string, style?: React.CSSProperties, keyPrefix = '') => {
      const segments = parseMessageForPaths(text);
      return segments.map((segment, i) => {
        if (segment.type === 'path' && segment.path) {
          return (
            <span
              key={`${keyPrefix}-${i}`}
              className="log-file-path"
              style={style}
              onClick={(e) => {
                e.stopPropagation();
                onNavigateLocation({
                  path: segment.path!,
                  line: segment.line || 1,
                  column: segment.column || 1,
                });
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  onNavigateLocation({
                    path: segment.path!,
                    line: segment.line || 1,
                    column: segment.column || 1,
                  });
                }
              }}
            >
              {segment.content}
            </span>
          );
        }
        return <span key={`${keyPrefix}-${i}`} style={style}>{segment.content}</span>;
      });
    };

    // Render message with ANSI colors and clickable file paths
    const renderMessageWithPaths = (message: string) => {
      // Check if message contains ANSI codes
      if (hasAnsiCodes(message)) {
        const ansiSegments = parseAnsi(message);
        return ansiSegments.map((segment: AnsiSegment, i: number) => {
          const style = ansiStyleToCss(segment.style);
          const hasStyle = Object.keys(style).length > 0;
          // Render each ANSI segment, then parse for file paths within it
          return (
            <span key={`ansi-${i}`} style={hasStyle ? style : undefined}>
              {renderPathSegment(segment.text, undefined, `ansi-${i}`)}
            </span>
          );
        });
      }

      // No ANSI codes, just parse for file paths
      return renderPathSegment(message, undefined, 'plain');
    };

    if (variant === 'terminal') {
      return (
        <div
          style={{ position: 'absolute', top: (startIndex + idx) * itemSize, left: 0, right: 0, height: itemSize }}
          className={`terminal-line ${log.level} ${clickable ? 'clickable' : ''}`}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : -1}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          <span className="term-time">[{log.timestamp}]</span>
          <span className={`term-task ${log.level}`}>{log.task}</span>
          <span className="term-text">{renderMessageWithPaths(log.message)}</span>
        </div>
      );
    }

    return (
      <div
        style={{ position: 'absolute', top: (startIndex + idx) * itemSize, left: 0, right: 0, height: itemSize }}
        className={`log-row ${log.level} ${clickable ? 'clickable' : ''}`}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : -1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <span className="pill small subtle">{log.timestamp}</span>
        <span className={`pill small ${log.level}`}>{log.task}</span>
        <div className="log-message">
          <div>{renderMessageWithPaths(log.message)}</div>
          {log.details && log.details.length > 0 && (
            <pre className="log-details">
              {log.details.map((detail, i) => (
                <div key={i}>{renderMessageWithPaths(detail)}</div>
              ))}
            </pre>
          )}
        </div>
      </div>
    );
  };

  if (!logs.length) {
    return <p className="muted">No logs yet</p>;
  }

  return (
    <div
      role="log"
      aria-label={variant === 'console' ? 'Console output' : 'Terminal output'}
      aria-live="polite"
      aria-relevant="additions"
      className={variant === 'console' ? 'log-list' : 'terminal-body'}
      style={{ position: 'relative', height: listHeight, overflow: 'auto' }}
      onScroll={handleScroll}
    >
      <div style={{ position: 'relative', height: totalHeight }}>
        {visible.map((log, idx) => (
          <Row key={log.id} log={log} idx={idx} />
        ))}
      </div>
    </div>
  );
});
